import {HTTP_STATUS_CODE} from 'definitions/constant'
import {unwrapAPIError} from 'utils'
import {getAnalyticsBaseUrl, requestHandler} from '../HTTP'
import {HttpMethods, EndPoints} from '../HTTP/HTTP.types'
import {
  AIUsageSummaryAPIResponse,
  AIUsageAppsAPIParams,
  AIUsageAppsAPIResponseData,
  AIUsageUsersAPIParams,
  AIUsageUsersAPIResponseData,
  AIUsageAppsPerUserAPIResponseData,
  AIUsageAppUsersAPIResponseData,
  AIUsageAppUrlsAPIResponseData,
  AIUsageExportResponse,
  AIUsageExportResult,
} from 'interfaces'
import Logger from 'services/Logger'

const analyticsBaseUrl = getAnalyticsBaseUrl()

const getAIUsageSummary = async (): Promise<AIUsageSummaryAPIResponse> => {
  try {
    const {data: responseData} =
      await requestHandler<AIUsageSummaryAPIResponse>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.AIUsageSummary}`,
      })
    return responseData
  } catch (error) {
    // Axios failures are owned by the interceptor (LogAPIError). Keep local
    // non-Axios failures (e.g. encodeURIComponent URIError) visible in Sentry.
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

const getAIUsageApps = async (
  params?: AIUsageAppsAPIParams,
): Promise<AIUsageAppsAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<AIUsageAppsAPIResponseData>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.AIUsageApps}`,
        options: {
          params: {
            ...params,
          },
        },
      })
    return responseData
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

const getAIUsageUsers = async (
  params?: AIUsageUsersAPIParams,
): Promise<AIUsageUsersAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<AIUsageUsersAPIResponseData>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.AIUsageUsers}`,
        options: {
          params: {
            ...params,
          },
        },
      })
    return responseData
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

const getAIUsageAppsPerUser = async (
  email: string,
): Promise<AIUsageAppsPerUserAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<AIUsageAppsPerUserAPIResponseData>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.AIUsageAppsPerUser.replace(
          '{email}',
          encodeURIComponent(email),
        )}`,
      })
    return responseData
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

const getAIUsageAppUsers = async (
  appName: string,
): Promise<AIUsageAppUsersAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<AIUsageAppUsersAPIResponseData>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.AIUsageAppUsers.replace(
          '{appName}',
          encodeURIComponent(appName),
        )}`,
      })
    return responseData
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

/** Domains to block for an app. Schema: GET /shadow-it/apps/{appName}/urls */
const getAIUsageAppUrls = async (appName: string): Promise<string[]> => {
  try {
    const {data: responseData} =
      await requestHandler<AIUsageAppUrlsAPIResponseData>({
        method: HttpMethods.GET,
        url: `${analyticsBaseUrl}${EndPoints.ShadowITAppUrls.replace(
          '{appName}',
          encodeURIComponent(appName),
        )}`,
      })
    return responseData.data.urls
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

const getAIUsageExport = async (): Promise<AIUsageExportResult> => {
  try {
    const response = await requestHandler<AIUsageExportResponse>({
      method: HttpMethods.GET,
      url: `${analyticsBaseUrl}${EndPoints.AIUsageExport}`,
    })

    if (response.status === HTTP_STATUS_CODE.NO_CONTENT) {
      return {isEmpty: true}
    }

    return {
      fileUrl: response.data?.data?.fileUrl,
      isEmpty: false,
    }
  } catch (error) {
    Logger.LogNonAxiosError(error)
    throw unwrapAPIError(error)
  }
}

export const AIUsageService = {
  getAIUsageSummary,
  getAIUsageApps,
  getAIUsageUsers,
  getAIUsageAppsPerUser,
  getAIUsageAppUsers,
  getAIUsageAppUrls,
  getAIUsageExport,
}
